/**
 * Ticket template editor: the printed business name, the header lines and
 * the footer lines — the parts of a ticket that are identity rather than
 * sale data. Everything else on the paper (folio, lines, totals, payments)
 * comes from the sale and is not editable here.
 *
 * The live preview is built by @equinox/shared's `buildTicket`, the same
 * function every printer backend consumes, over a fixed sample sale. It is
 * rendered node-by-node with the shared layout primitives (`wrapText`,
 * `renderRow`), so what the admin sees wraps and aligns exactly like the
 * 58 mm paper. Double-width type is emulated geometrically: a `large` text
 * node wraps at 16 columns and renders at 2em — and 16ch at 2em occupies
 * precisely the width of 32ch at 1em in a monospace font.
 * See docs/decisions/2026-08-01-ticket-template-editor.md.
 */

import { useEffect, useMemo, useState, type ReactElement } from 'react';
import type { AppConfig, TicketDocument } from '@equinox/shared';
import { buildTicket, renderRow, translator, wrapText } from '@equinox/shared';
import { useI18n } from '../../i18n/I18nContext.js';
import { configApi } from '../../api/resources.js';
import { ApiError } from '../../lib/apiClient.js';
import { Banner } from '../../components/Banner.js';

/**
 * Ticket label copy comes from the SHARED es-MX catalog — the exact strings
 * the terminal prints — not from the admin's own catalog. The preview must
 * show the paper's words even if the admin browses in another language.
 */
const ticketT = translator('es-MX');

const PREVIEW_LABELS = {
  folio: ticketT('ticket.folio'),
  date: ticketT('ticket.date'),
  attendedBy: ticketT('ticket.attendedBy'),
  terminal: ticketT('ticket.terminal'),
  subtotal: ticketT('ticket.subtotal'),
  discount: ticketT('ticket.discount'),
  total: ticketT('ticket.total'),
  cash: ticketT('ticket.cash'),
  card: ticketT('ticket.card'),
  credit: ticketT('ticket.credit'),
  debit: ticketT('ticket.debit'),
  auth: ticketT('ticket.auth'),
  tendered: ticketT('ticket.tendered'),
  change: ticketT('ticket.change'),
  items: ticketT('ticket.items'),
  reprint: ticketT('ticket.reprint'),
  voided: ticketT('ticket.voided'),
  copy: ticketT('ticket.copy'),
};

function buildSampleTicket(params: {
  columns: number;
  businessName: string;
  header: string[];
  footer: string[];
  qrUrl: string;
}): TicketDocument {
  return buildTicket({
    columns: params.columns,
    businessName: params.businessName,
    header: params.header,
    footer: params.footer,
    qrUrl: params.qrUrl,
    folio: 'CP-000123',
    soldAt: new Date(),
    staffName: 'Ana',
    terminalName: 'Caja principal',
    lines: [
      {
        name: 'Mermelaana Fresa 200 g',
        qty: 1,
        unitPriceCents: 14000,
        discountCents: 0,
        totalCents: 14000,
      },
      {
        name: 'Salsaana Ignis 50 g',
        qty: 2,
        unitPriceCents: 9000,
        discountCents: 0,
        totalCents: 18000,
      },
    ],
    subtotalCents: 32000,
    discountCents: 0,
    totalCents: 32000,
    payments: [{ method: 'cash', amountCents: 32000, tenderedCents: 50000, changeCents: 18000 }],
    isReprint: false,
    copyNo: 1,
    labels: PREVIEW_LABELS,
  });
}

/**
 * Node-aware HTML flattening. `renderTicketToLines` would center a `large`
 * chunk inside 16 character cells and lose the doubled glyph width; here the
 * geometry survives because the font size doubles with the halved column
 * count.
 */
function TicketPreview({ doc }: { doc: TicketDocument }) {
  const out: ReactElement[] = [];
  doc.nodes.forEach((node, i) => {
    switch (node.type) {
      case 'text': {
        const wide = node.size === 'large' || node.size === 'xlarge';
        const width = wide ? Math.floor(doc.columns / 2) : doc.columns;
        for (const [j, chunk] of wrapText(node.value, width).entries()) {
          out.push(
            <div
              key={`${i}-${j}`}
              className={[
                'ticket-preview__line',
                wide ? 'ticket-preview__line--wide' : '',
                node.bold ? 'ticket-preview__line--bold' : '',
                `ticket-preview__line--${node.align ?? 'left'}`,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {chunk || ' '}
            </div>,
          );
        }
        break;
      }
      case 'row':
        out.push(
          <div
            key={i}
            className={`ticket-preview__line${node.bold ? ' ticket-preview__line--bold' : ''}`}
          >
            {renderRow(node.left, node.right, doc.columns)}
          </div>,
        );
        break;
      case 'rule':
        out.push(
          <div key={i} className="ticket-preview__line">
            {(node.char ?? '-').repeat(doc.columns)}
          </div>,
        );
        break;
      case 'feed':
        for (let j = 0; j < node.lines; j += 1) {
          out.push(
            <div key={`${i}-${j}`} className="ticket-preview__line">
              {' '}
            </div>,
          );
        }
        break;
      case 'qr':
        // The paper prints a real QR; the preview shows an honest
        // placeholder box with the target underneath, so the admin can
        // verify WHAT it points at even though this panel doesn't render
        // scannable modules.
        out.push(
          <div key={i} className="ticket-preview__qr">
            <span className="ticket-preview__qr-box" aria-hidden="true">
              QR
            </span>
            <span className="ticket-preview__qr-value">{node.value}</span>
          </div>,
        );
        break;
      case 'cut':
        break;
    }
  });
  return <div className="ticket-preview">{out}</div>;
}

/** Textarea rows ↔ config array. Verbatim, minus CR and trailing blanks. */
function textToLines(text: string): string[] {
  const lines = text.split('\n').map((l) => l.replace(/\r$/, ''));
  while (lines.length > 0 && lines[lines.length - 1]?.trim() === '') lines.pop();
  return lines;
}

export function TicketPage() {
  const { t } = useI18n();
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [businessName, setBusinessName] = useState('');
  const [headerText, setHeaderText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const loaded = await configApi.get();
        setConfig(loaded);
        setBusinessName(loaded.businessName);
        setHeaderText(loaded.ticketHeader.join('\n'));
        setFooterText(loaded.ticketFooter.join('\n'));
        setQrUrl(loaded.ticketQrUrl ?? '');
      } catch (err) {
        setError(err instanceof ApiError ? err.message : t('common.error'));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  const header = useMemo(() => textToLines(headerText), [headerText]);
  const footer = useMemo(() => textToLines(footerText), [footerText]);
  const columns = config?.printerColumns ?? 32;

  const dirty =
    config !== null &&
    (businessName !== config.businessName ||
      header.join('\n') !== config.ticketHeader.join('\n') ||
      footer.join('\n') !== config.ticketFooter.join('\n') ||
      qrUrl.trim() !== (config.ticketQrUrl ?? ''));

  const doc = useMemo(
    () =>
      buildSampleTicket({
        columns,
        businessName: businessName || ' ',
        header,
        footer,
        qrUrl: qrUrl.trim(),
      }),
    [columns, businessName, header, footer, qrUrl],
  );

  async function save() {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await configApi.update({
        businessName: businessName.trim() || config?.businessName,
        ticketHeader: header,
        ticketFooter: footer,
        ticketQrUrl: qrUrl.trim(),
      });
      setConfig(updated);
      setBusinessName(updated.businessName);
      setHeaderText(updated.ticketHeader.join('\n'));
      setFooterText(updated.ticketFooter.join('\n'));
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div className="page-header">
        <div>
          <h1>{t('ticket.title')}</h1>
          <p>{t('ticket.subtitle')}</p>
        </div>
        <button
          type="button"
          className="btn btn--primary"
          disabled={!dirty || saving}
          onClick={() => void save()}
        >
          {t('ticket.save')}
        </button>
      </div>

      {error && <Banner kind="danger">{error}</Banner>}
      {saved && !dirty && <Banner kind="ok">{t('ticket.saved')}</Banner>}
      {dirty && <Banner kind="warn">{t('ticket.unsaved')}</Banner>}

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : (
        <div className="ticket-editor">
          <div className="ticket-editor__form">
            <label htmlFor="ticket-business-name">
              {t('ticket.businessName')}
              <input
                id="ticket-business-name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                maxLength={64}
              />
              <span className="field-hint">{t('ticket.businessNameHint')}</span>
            </label>

            <label htmlFor="ticket-header">
              {t('ticket.headerLabel')}
              <textarea
                id="ticket-header"
                rows={3}
                value={headerText}
                onChange={(e) => setHeaderText(e.target.value)}
                spellCheck={false}
              />
              <span className="field-hint">{t('ticket.headerHint')}</span>
            </label>

            <label htmlFor="ticket-qr-url">
              {t('ticket.qrUrl')}
              <input
                id="ticket-qr-url"
                type="url"
                value={qrUrl}
                onChange={(e) => setQrUrl(e.target.value)}
                maxLength={200}
                placeholder="https://"
                spellCheck={false}
              />
              <span className="field-hint">{t('ticket.qrUrlHint')}</span>
            </label>

            <label htmlFor="ticket-footer">
              {t('ticket.footerLabel')}
              <textarea
                id="ticket-footer"
                rows={8}
                value={footerText}
                onChange={(e) => setFooterText(e.target.value)}
                spellCheck={false}
              />
              <span className="field-hint">{t('ticket.footerHint')}</span>
            </label>
          </div>

          <div className="ticket-editor__preview">
            <h2>{t('ticket.preview')}</h2>
            <p className="field-hint">{t('ticket.previewHint')}</p>
            <TicketPreview doc={doc} />
          </div>
        </div>
      )}
    </div>
  );
}
